import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { HardHat, Search, Loader2, CheckCircle2, Circle, Clock, MapPin, Tag, User,
  XCircle, Star, ArrowLeft, PartyPopper,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { LegalFooter } from "@/components/LegalFooter";

export default function Tracking() {
  const { reference: refParam } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState(refParam || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchTrack = useCallback(async (r) => {
    if (!r) return;
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(r)}`);
      setData(data);
    } catch (e) {
      setData(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (refParam) fetchTrack(refParam);
  }, [refParam, fetchTrack]);

  const search = (e) => {
    e.preventDefault();
    if (ref.trim()) navigate(`/suivi/${ref.trim()}`);
  };

  return (
    <div className="min-h-screen bg-[#0B1E4D] flex flex-col" data-testid="tracking-page">
      <header className="max-w-3xl w-full mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center">
          <Logo flagClass="h-8" textClass="text-xl" boxed />
        </button>
        <button onClick={() => navigate("/")} className="text-sm text-slate-300 hover:text-white flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Accueil
        </button>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-5 py-8">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 sm:p-8">
          <h1 className="font-display text-2xl font-bold text-slate-900">Suivi de votre demande</h1>
          <p className="text-sm text-slate-500 mt-1">Saisissez votre référence pour suivre l'avancement en direct.</p>
          <form onSubmit={search} className="mt-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input className="pl-9" placeholder="Ex: BC-2026-0001" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="track-reference-input" />
            </div>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="track-search-button">Suivre</Button>
          </form>
        </div>

        {loading && <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-400" /></div>}

        {notFound && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <XCircle className="h-10 w-10 mx-auto text-rose-400" />
            <p className="mt-3 text-slate-600">Aucune demande trouvée pour cette référence. Vérifiez votre saisie.</p>
          </div>
        )}

        {data && !loading && <TrackDetail data={data} onRated={() => fetchTrack(data.reference)} />}
      </main>
      <LegalFooter />
    </div>
  );
}

function TrackDetail({ data, onRated }) {
  return (
    <div className="mt-6 space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="track-result">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono-nums text-xs font-bold text-slate-400">{data.reference}</p>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-1">{data.category} · {data.city}</h2>
          </div>
          {data.cancelled ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300 px-3 py-1 text-sm font-semibold"><XCircle className="h-4 w-4" /> Annulé</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 text-blue-700 border border-blue-300 px-3 py-1 text-sm font-semibold">{data.status}</span>
          )}
        </div>

        <div className="mt-4 space-y-1.5 text-sm text-slate-600">
          <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> {data.full_name}</div>
          <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-slate-400" /> {data.category}</div>
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {data.city}</div>
          {data.artisan_name && <div className="flex items-center gap-2"><HardHat className="h-4 w-4 text-slate-400" /> Artisan : <strong className="text-slate-800">{data.artisan_name}</strong></div>}
        </div>

        {/* Timeline */}
        {!data.cancelled && (
          <div className="mt-6" data-testid="track-timeline">
            <ol className="relative border-l-2 border-slate-200 ml-3">
              {data.timeline.map((step) => (
                <li key={step.status} className="mb-5 ml-6">
                  <span className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full ${step.reached ? "bg-emerald-500" : "bg-slate-200"}`}>
                    {step.reached ? <CheckCircle2 className="h-4 w-4 text-white" /> : <Circle className="h-3 w-3 text-slate-400" />}
                  </span>
                  <p className={`font-medium ${step.reached ? "text-slate-900" : "text-slate-400"}`}>{step.status}</p>
                  {step.timestamp && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" /> {new Date(step.timestamp).toLocaleDateString("fr-FR")}</p>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Rating */}
      {data.rating ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="track-rating-done">
          <p className="font-display font-semibold text-slate-900">Votre évaluation</p>
          <div className="flex items-center gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`h-6 w-6 ${n <= data.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            ))}
          </div>
          {data.rating_comment && <p className="mt-3 text-sm text-slate-600 italic">« {data.rating_comment} »</p>}
          <p className="mt-2 text-sm text-emerald-600 font-medium">Merci pour votre retour !</p>
        </div>
      ) : data.can_rate ? (
        <RatingForm reference={data.reference} onRated={onRated} />
      ) : null}
    </div>
  );
}

function RatingForm({ reference, onRated }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!rating) {
      toast.error("Merci de sélectionner une note");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/track/${encodeURIComponent(reference)}/rating`, { rating, comment });
      toast.success("Merci pour votre évaluation !");
      onRated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="track-rating-form">
      <div className="flex items-center gap-2">
        <PartyPopper className="h-5 w-5 text-blue-600" />
        <p className="font-display font-semibold text-slate-900">Votre chantier est terminé !</p>
      </div>
      <p className="text-sm text-slate-500 mt-1">Comment évaluez-vous la prestation de l'artisan ?</p>
      <div className="flex items-center gap-1 mt-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} data-testid={`rating-star-${n}`}>
            <Star className={`h-9 w-9 transition-colors ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <Label className="text-sm mt-4 block">Commentaire (optionnel)</Label>
      <Textarea className="mt-1.5" placeholder="Partagez votre expérience..." value={comment} onChange={(e) => setComment(e.target.value)} data-testid="rating-comment-input" />
      <Button onClick={submit} disabled={saving} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="rating-submit-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer mon évaluation"}
      </Button>
    </div>
  );
}
